import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { BarChart3, Users, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const statusColors = {
  inquiry: '#3B82F6',
  onboarding: '#F59E0B',
  predecode_pending: '#F97316',
  waiting_tests: '#8B5CF6',
  decode_in_progress: '#6366F1',
  decode_delivered: '#06B6D4',
  awaiting_subscription: '#EAB308',
  active_phase: '#10B981',
  report_due: '#F43F5E',
  renewal_due: '#EF4444'
};

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [renewals, setRenewals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, renewalsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/renewals`)
      ]);
      setAnalytics(analyticsRes.data);
      setRenewals(renewalsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusData = analytics?.status_counts 
    ? Object.entries(analytics.status_counts)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          name: status.replace(/_/g, ' '),
          value: count,
          color: statusColors[status] || '#6B7280'
        }))
    : [];

  const barChartData = analytics?.status_counts
    ? Object.entries(analytics.status_counts).map(([status, count]) => ({
        name: status.replace(/_/g, ' ').split(' ').map(w => w[0].toUpperCase()).join(''),
        fullName: status.replace(/_/g, ' '),
        count,
        fill: statusColors[status] || '#6B7280'
      }))
    : [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 text-[#C89B7B] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary" data-testid="analytics-title">
            Analytics & Renewal
          </h1>
          <p className="text-adm-tertiary text-adm-primary/60">Platform insights and metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-adm-tertiary text-adm-primary/50">Total Subscribers</p>
                  <p className="text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary mt-1">
                    {analytics?.total_subscribers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#C89B7B]/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#C89B7B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-adm-tertiary text-adm-primary/50">Active Phase</p>
                  <p className="text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary mt-1">
                    {analytics?.active_subscribers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#4E7A6B]/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#4E7A6B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-adm-tertiary text-adm-primary/50">Recent Inquiries</p>
                  <p className="text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary mt-1">
                    {analytics?.recent_inquiries || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-adm-tertiary text-adm-primary/50">Renewals Due</p>
                  <p className="text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary mt-1">
                    {renewals?.renewal_due_count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Status Distribution Pie */}
          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-adm-primary text-adm-primary font-['Outfit']">Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {statusData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#131B2B', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-adm-tertiary text-adm-primary/50">
                  No data available
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                {statusData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-adm-secondary text-adm-primary/60 capitalize">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status Bar Chart */}
          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-adm-primary text-adm-primary font-['Outfit']">Journey Stages</CardTitle>
            </CardHeader>
            <CardContent>
              {barChartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis type="number" stroke="#9CA3AF" />
                      <YAxis dataKey="name" type="category" stroke="#9CA3AF" width={40} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#131B2B', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-adm-tertiary text-adm-primary/50">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Renewal List */}
        {renewals?.subscribers?.length > 0 && (
          <Card className="admin-card bg-adm-surface border-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-adm-primary text-adm-primary font-['Outfit'] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Subscribers Due for Renewal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {renewals.subscribers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 rounded-lg bg-adm-bg bg-adm-bg">
                    <div>
                      <p className="font-medium text-adm-primary text-adm-primary">{user.name}</p>
                      <p className="text-sm text-adm-tertiary text-adm-primary/50">{user.email}</p>
                    </div>
                    <Badge className="bg-red-500/20 text-red-400">Renewal Due</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
